"use client";

/**
 * Inline rich text field for the canvas.
 *
 * Public view and idle builder fields render static {@link SanitizedHtml}. Clicking a builder field
 * mounts the on-demand editor ({@link InlineTipTapEditor}). The editor is deliberately NOT
 * instantiated in this component so `useEditor` never runs on the public page — a mounted
 * @tiptap/react v3 editor loops on a 1ms timer and leaks memory (see InlineTipTapEditor for the
 * mechanism); with one field per inline editor that crashed public landing pages.
 */

import type React from "react";
import { useCallback } from "react";
import dynamic from "next/dynamic";

import { stripHtmlTags } from "@/lib/utils/sanitization";
import { useInlineEdit } from "../../../../_hooks/useInlineEdit";
import { DEFAULT_INLINE_PLACEHOLDER } from "../../../../_logic/constants";
import { SanitizedHtml } from "./SanitizedHtml";

// Code-split the editor: it only renders in the `editing` branch (never on the public/idle path), so
// `ssr: false` is safe and keeps the entire @tiptap bundle out of the public landing-page chunk.
const InlineTipTapEditor = dynamic(() => import("./InlineTipTapEditor").then((m) => m.InlineTipTapEditor), {
  ssr: false,
});

/** TipTap saves an empty editor as `<p></p>` or `<p><br></p>` — treat those as empty. */
function isHtmlEmpty(html: string): boolean {
  if (!html) return true;
  return (
    stripHtmlTags(html)
      .replace(/&nbsp;/g, " ")
      .trim() === ""
  );
}

interface InlineTipTapEditProps {
  /** Current HTML content */
  content: string;
  /** Tailwind/prose classes for the display wrapper */
  className?: string;
  /** Component ID that owns this field */
  componentId: string;
  /** Field ID for inline edit tracking (e.g., "hero-description") */
  fieldId: string;
  /** Component property name to update (e.g., "description", "content") */
  field: string;
  /** Placeholder when content is empty */
  placeholder?: string;
}

/**
 * Inline rich text field for the canvas. Renders static HTML in the public view and in the
 * builder's display state; mounts {@link InlineTipTapEditor} only while the field is being edited.
 *
 * @param props - See {@link InlineTipTapEditProps}.
 * @returns The static content, an empty fragment, the click-to-edit display, or the active editor.
 */
export function InlineTipTapEdit({
  content,
  className,
  componentId,
  fieldId,
  field,
  placeholder = DEFAULT_INLINE_PLACEHOLDER,
}: InlineTipTapEditProps): React.ReactElement {
  // Hooks
  const { startEditing, isEditing, isPublic } = useInlineEdit(componentId);

  // Derived values
  const editing = isEditing(fieldId);
  const isEmpty = isHtmlEmpty(content);

  // Handlers
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPublic || editing) return;
      e.stopPropagation();
      startEditing(fieldId);
    },
    [isPublic, editing, fieldId, startEditing]
  );

  // Render — public view: static, no editor ever mounted
  if (isPublic) {
    if (isEmpty) return <></>;
    return <SanitizedHtml html={content} className={className} />;
  }

  // Builder — active edit: mount the on-demand editor for this field only
  if (editing) {
    return (
      <InlineTipTapEditor
        initialContent={content}
        className={className}
        componentId={componentId}
        field={field}
        placeholder={placeholder}
      />
    );
  }

  // Builder — empty display: click to add
  if (isEmpty) {
    return (
      <div
        className="cursor-pointer rounded px-2 py-1 font-medium text-gray-400 transition-opacity hover:opacity-80"
        onClick={handleClick}
      >
        {placeholder}
      </div>
    );
  }

  // Builder — display mode: click to edit
  return (
    <div className="cursor-pointer transition-opacity hover:opacity-80" onClick={handleClick}>
      <SanitizedHtml html={content} className={className} />
    </div>
  );
}
